import type { ComponentProps } from 'react';
import { useState } from 'react';
import type { GestureResponderEvent } from 'react-native';
import Box from '@/components/UI/Box';
import Checkbox from '@/components/UI/Checkbox';
import PressableRow from './PressableRow';

interface CheckboxRowProps
  extends Omit<
    ComponentProps<typeof PressableRow>,
    'accessibilityRole' | 'children' | 'onPress'
  > {
  defaultValue?: boolean;
  value?: boolean;
  onValueChange?: (value: boolean) => void;
  children: React.ReactNode;
  variant?: ComponentProps<typeof Checkbox>['variant'];
}

export default function CheckboxRow({
  defaultValue = false,
  value,
  onValueChange,
  variant,
  children,
  disabled,
  ...rowProps
}: CheckboxRowProps) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const checked = value ?? uncontrolledValue;

  const onPress = (event?: GestureResponderEvent) => {
    event?.stopPropagation();

    const nextValue = !checked;

    if (value === undefined) {
      setUncontrolledValue(nextValue);
    }

    onValueChange?.(nextValue);
  };

  return (
    <PressableRow
      {...rowProps}
      aria-checked={checked}
      accessibilityState={{ checked, disabled }}
      disabled={disabled}
      onPress={onPress}
    >
      <Box flex>{children}</Box>

      <Box pointerEvents="none">
        <Checkbox value={checked} disabled={disabled} variant={variant} />
      </Box>
    </PressableRow>
  );
}
